import { Check, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { copyToClipboard } from "~/utils";
import { cn } from "~/utils/cn";

interface ChatCopyButtonProps {
  text: string;
  copiedMessage: string;
  ariaLabel: string;
  className?: string;
}

// The check-icon swap is the primary feedback: the copy toast renders at the app root, which a
// popped-out chat window (a portal) can't show — same constraint as the composer's in-panel notice.
export function ChatCopyButton({ text, copiedMessage, ariaLabel, className }: ChatCopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const handleCopy = async () => {
    const didCopy = await copyToClipboard(text, copiedMessage);
    if (!didCopy) return;
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1500);
  };

  const Icon = copied ? Check : Copy;

  return (
    <button
      type="button"
      onClick={() => void handleCopy()}
      aria-label={ariaLabel}
      className={cn(
        "text-muted-foreground hover:text-foreground shrink-0 transition-colors",
        copied && "text-success hover:text-success",
        className,
      )}
    >
      <Icon className="size-3" aria-hidden="true" />
    </button>
  );
}
